import {
    getCurrentMonthDates,
    getCurrentWeek,
    getEndOfDayDate,
    getLastWeekDates,
    getPreviousMonthDates,
    getStartOfDayDate,
    getYesterdayDate,
    setDateTime
} from "@dropDesk/utils/helpers/date_helper";
import {ConstantsKeys} from "@dropDesk/domain/entities/constants/constants_keys";

export enum ComparativePresetEnum {
    todayVsYesterday = 'todayVsYesterday',
    currentWeekVsPrevious = 'currentWeekVsPrevious',
    currentMonthVsPrevious = 'currentMonthVsPrevious',
    morningVsAfternoon = 'morningVsAfternoon',
}

export type ComparativePeriodRange = [Date | null, Date | null];

export type ComparativeDateParams = {
    period: ComparativePeriodRange;
    comparePeriod: ComparativePeriodRange;
    preset: ComparativePresetEnum | null;
};

const serializeDateRange = (range: ComparativePeriodRange): [string | null, string | null] => {
    return [
        range[0] ? range[0].toISOString() : null,
        range[1] ? range[1].toISOString() : null,
    ];
};

const deserializeDateRange = (range: [string | null, string | null]): ComparativePeriodRange => {
    return [
        range[0] ? new Date(range[0]) : null,
        range[1] ? new Date(range[1]) : null,
    ];
};

const isValidPreset = (preset: string | null | undefined): preset is ComparativePresetEnum => {
    return Object.values(ComparativePresetEnum).includes(preset as ComparativePresetEnum);
};

export const getComparativePeriodFromPreset = (preset: ComparativePresetEnum): ComparativeDateParams => {
    const today = new Date();

    if (preset === ComparativePresetEnum.todayVsYesterday) {
        const yesterday = getYesterdayDate();
        return {
            period: [getStartOfDayDate(today), getEndOfDayDate(today)],
            comparePeriod: [getStartOfDayDate(yesterday), getEndOfDayDate(yesterday)],
            preset,
        };
    }

    if (preset === ComparativePresetEnum.currentWeekVsPrevious) {
        const currentWeek = getCurrentWeek();
        const lastWeek = getLastWeekDates();
        return {
            period: [getStartOfDayDate(currentWeek[0]), getEndOfDayDate(currentWeek[1])],
            comparePeriod: [getStartOfDayDate(lastWeek[0]), getEndOfDayDate(lastWeek[1])],
            preset,
        };
    }

    if (preset === ComparativePresetEnum.currentMonthVsPrevious) {
        const currentMonth = getCurrentMonthDates();
        const previousMonth = getPreviousMonthDates();
        return {
            period: [getStartOfDayDate(currentMonth[0]), getEndOfDayDate(currentMonth[1])],
            comparePeriod: [getStartOfDayDate(previousMonth[0]), getEndOfDayDate(previousMonth[1])],
            preset,
        };
    }

    return {
        period: [
            setDateTime(today, 12, 0, 0),
            setDateTime(today, 18, 0, 0),
        ],
        comparePeriod: [
            setDateTime(today, 6, 0, 0),
            setDateTime(today, 12, 0, 0),
        ],
        preset: ComparativePresetEnum.morningVsAfternoon,
    };
};

export const getDefaultComparativeDateParams = (): ComparativeDateParams => {
    return getComparativePeriodFromPreset(ComparativePresetEnum.currentWeekVsPrevious);
};

export const setComparativeDateParams = (params: ComparativeDateParams): void => {
    localStorage.setItem(
        ConstantsKeys.comparativeDateParams,
        JSON.stringify({
            period: serializeDateRange(params.period),
            comparePeriod: serializeDateRange(params.comparePeriod),
            preset: params.preset,
        })
    );
};

export const getComparativeDateParams = (): ComparativeDateParams => {
    const stored = localStorage.getItem(ConstantsKeys.comparativeDateParams);
    if (!stored) {
        return getDefaultComparativeDateParams();
    }

    const parsed = JSON.parse(stored) as {
        period: [string | null, string | null];
        comparePeriod: [string | null, string | null];
        preset: ComparativePresetEnum | null | string;
    };

    if (!isValidPreset(parsed.preset)) {
        return {
            period: deserializeDateRange(parsed.period),
            comparePeriod: deserializeDateRange(parsed.comparePeriod),
            preset: null,
        };
    }

    return getComparativePeriodFromPreset(parsed.preset);
};

export const COMPARATIVE_PRESET_LABELS: Record<ComparativePresetEnum, string> = {
    [ComparativePresetEnum.todayVsYesterday]: 'Hoje x Ontem',
    [ComparativePresetEnum.currentWeekVsPrevious]: 'Esta semana x Anterior',
    [ComparativePresetEnum.currentMonthVsPrevious]: 'Este mês x Anterior',
    [ComparativePresetEnum.morningVsAfternoon]: 'Tarde x Manhã',
};
